import { Link } from "react-router-dom";
import {
  Lock, MagnifyingGlass, ListChecks, Broadcast, Scales,
  ArrowRight, X, Sparkle,
} from "@phosphor-icons/react";
import { Button } from "./ui/button";
import { useAuth } from "../lib/AuthContext";

/**
 * Modal shown to anonymous visitors after their 3rd free NPI search.
 * Triggered by consumers that call /api/public/track-search and see
 * `reached_limit: true` in the response.
 */
export default function AnonymousSignupGate({ open, onClose, searchCount = 3 }) {
  const { login, signup } = useAuth();
  if (!open) return null;

  const unlocks = [
    { Icon: MagnifyingGlass, text: "100 NPI lookups per day on Free" },
    { Icon: ListChecks, text: "Bulk screening — 25 NPIs per run, 2 runs/day" },
    { Icon: Broadcast, text: "Monitor up to 25 providers for compliance changes" },
    { Icon: Scales, text: "Roster reconciliation workflow (no run limits)" },
  ];

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      data-testid="anonymous-signup-gate"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" />
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-slate-400 hover:text-slate-700 p-1 z-10"
          aria-label="Close"
          data-testid="anon-gate-close"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="bg-gradient-to-br from-navy to-navy/90 text-white px-6 py-8 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-white/10 rounded-2xl mb-4">
            <Lock className="w-7 h-7" weight="fill" />
          </div>
          <h2 className="font-display font-bold text-2xl mb-2">
            You've used your free searches
          </h2>
          <p className="text-white/80 text-sm leading-relaxed max-w-sm mx-auto">
            Create a free account to continue searching NPIs and unlock bulk screening, monitoring, and reconciliation.
          </p>
        </div>

        <div className="px-6 py-5">
          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-3 flex items-center gap-1.5">
            <Sparkle className="w-3.5 h-3.5 text-amber-500" weight="fill" />
            Free account unlocks
          </p>
          <ul className="space-y-2.5 mb-5">
            {unlocks.map(({ Icon, text }, i) => (
              <li key={i} className="flex items-start gap-2.5 text-sm text-slate-700">
                <Icon className="w-4 h-4 shrink-0 mt-0.5 text-teal" weight="fill" />
                <span>{text}</span>
              </li>
            ))}
          </ul>

          <Button
            onClick={() => signup()}
            className="w-full bg-teal hover:bg-teal/90 text-white rounded-xl gap-2 h-11 font-semibold"
            data-testid="anon-gate-signup"
          >
            Sign up free <ArrowRight className="w-4 h-4" />
          </Button>
          <button
            onClick={() => login()}
            className="w-full text-sm text-slate-500 hover:text-slate-900 mt-3 py-2 font-medium"
            data-testid="anon-gate-login"
          >
            Already have an account? Log in
          </button>
          <p className="text-[11px] text-slate-400 text-center mt-4">
            No credit card required ·{" "}
            <Link to="/pricing" className="underline hover:text-slate-700">See pricing</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
